import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId } from '@/hooks'
import { Button, SettingsSubheading, TextField } from '@/ui'
import {
	organisationIndexQueryKey,
	organisationRelationShowQueryKey,
	renameOrganisationDataSchema,
	useOrganisationRelationShow,
	useOrganisationRename,
} from '@jobilla/nexus-sdk'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export function CompanyName() {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const organisationQuery = useOrganisationRelationShow(organisationId, { enabled: !!organisationId })

	return (
		<RequireAccess permission="organisation:rename">
			<section>
				<SettingsSubheading>{t('companyName.title')}</SettingsSubheading>
				<p className="max-w-[600px] mb-8 text-gray-600">{t('companyName.description')}</p>
				{organisationQuery.data && (
					<CompanyNameForm organisationId={organisationId} initialName={organisationQuery.data.name} />
				)}
			</section>
		</RequireAccess>
	)
}

function CompanyNameForm({ organisationId, initialName }: { organisationId: string; initialName: string }) {
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [justSaved, setJustSaved] = useState(false)

	const renameMutation = useOrganisationRename()

	const companyNameSchema = useMemo(() =>
		renameOrganisationDataSchema.extend({
			name: z.string().trim().min(1, t('companyName.required')).max(255, t('companyName.tooLong')),
		}), [t])

	const form = useForm({
		defaultValues: { name: initialName },
		validators: { onChange: companyNameSchema },
		onSubmit: async ({ value }) => {
			const savedName = value.name.trim()

			const updated = await renameMutation.mutateAsync({
				organisation: organisationId,
				data: { name: savedName },
			})

			await Promise.all([
				queryClient.invalidateQueries({ queryKey: organisationIndexQueryKey() }),
				queryClient.invalidateQueries({ queryKey: organisationRelationShowQueryKey(organisationId) }),
			])

			form.reset({ name: savedName })
			setJustSaved(true)

			// The slug changes with the name; keep the URL in sync so the slug-keyed switcher resolves.
			await navigate({
				to: '/company/$companyId/settings/organization',
				params: { companyId: updated.slug },
				replace: true,
			})
		},
	})

	return (
		<form
			onSubmit={(event) => {
				event.preventDefault()
				void form.handleSubmit()
			}}
			className="max-w-[800px] space-y-4"
		>
			<form.Subscribe>
				{({ isDirty, canSubmit, isSubmitting }) => (
					<>
						<form.Field name="name">
							{({ state, handleChange, handleBlur }) => (
								<TextField
									label={t('companyName.label')}
									value={state.value}
									onChange={(value) => {
										handleChange(value)
										setJustSaved(false)
									}}
									onBlur={handleBlur}
									placeholder={t('companyName.placeholder')}
									isWaiting={isSubmitting}
									errors={[...state.meta.errors, renameMutation.error?.message]}
								/>
							)}
						</form.Field>
						<div className="flex items-center gap-3">
							<Button
								type="committal"
								size="short"
								submit={true}
								disabled={!isDirty || !canSubmit || isSubmitting}
								isLoading={isSubmitting}
							>
								{justSaved ? t('companyName.saved') : t('companyName.save')}
							</Button>
							{isDirty && !isSubmitting && (
								<span className="text-sm text-grey-500">{t('companyName.unsavedChanges')}</span>
							)}
						</div>
					</>
				)}
			</form.Subscribe>
		</form>
	)
}
